import { replace } from 'connected-react-router';
import isEmpty from 'lodash/isEmpty';
import { call, put, takeLatest } from 'redux-saga/effects';
import { getCurrentUserData } from 'utils/helper';
import AuthService from '../../common/auth.service';
import { api } from '../../utils/axios';
import { API_BASE_URL, USER_ROLE } from '../../utils/constants';
import { SMKLinks } from '../../utils/links';
import { doHRLogin, doLogin, doLogout, doSocialLogin, getUser } from './actions';

export const LOGIN_API_URL = `${API_BASE_URL}/user/login`;
export const LOGOUT_API_URL = `${API_BASE_URL}/user/logout`;
export const GET_USER_API_URL = `${API_BASE_URL}/user/me`;
export const SOCIAL_LOGIN_API_URL = `${API_BASE_URL}/user/social-login`;

export const getUserAPIKey = (source) => {
  const { apiKey } = getCurrentUserData(source);
  return {
    'x-api-key': apiKey,
  };
};

const loginCall = (email, password, userType) => {
  const { apiURL } = getCurrentUserData(userType);

  return api(
    {
      method: 'POST',
      url: apiURL + '/user/login',
      data: {
        email,
        password,
        device: 0,
      },
    },
    {
      ...getUserAPIKey(userType),
    }
  );
};

const logoutCall = () =>
  api({
    method: 'POST',
    url: LOGOUT_API_URL,
  });

const getUserCall = (userType) => {
  const { apiURL } = getCurrentUserData(userType);

  return api(
    {
      method: 'GET',
      url: apiURL + '/user/me',
    },
    {
      ...getUserAPIKey(userType),
    }
  );
};

const socialLoginCall = (payload, userType) => {
  const { apiURL } = getCurrentUserData(userType);

  return api(
    {
      method: 'POST',
      url: apiURL + '/user/social-login',
      data: {
        ...payload,
      },
    },
    {
      ...getUserAPIKey(userType),
    }
  );
};

function* doLoginRequest(action) {
  try {
    const auth = new AuthService();

    const removeAllCookies = () => {
      auth.unsetSessionCookie();
      auth.unsetRefreshSessionCookie();
      auth.unsetAppSourceCookie();
    };

    yield put(doLogin.request({ isLoading: true }));

    const { email, password, userType } = action.payload;

    let updatedUserType = userType;

    const response = yield call(loginCall, email, password, updatedUserType);

    if (response?.success !== false) {
      const { accessToken, refreshToken } = response;
      if (accessToken && refreshToken) {
        // set token
        auth.setSessionCookie(accessToken);
        auth.setRefreshSessionCookie(refreshToken);

        const user = yield call(getUserCall, updatedUserType);

        if (!isEmpty(user)) {
          auth.setAppSourceCookie(user?.appSource);

          const allowUsers = [USER_ROLE.ADMIN, USER_ROLE.STAFF];
          if (allowUsers.includes(user.role)) {
            yield put(doLogin.success({ token: accessToken }));
            yield put(getUser.success({ user, appSource: user?.appSource }));

            yield put(replace(SMKLinks.DASHBOARD));
          } else {
            alert('Not Authorised');
            removeAllCookies();
          }
        } else {
          removeAllCookies();
        }
      }
    } else {
      yield put(doLogin.failure('Something went wrong'));
    }
  } catch (error) {
    yield put(doLogin.failure(error));
  } finally {
    yield put(doLogin.fulfill({ isLoading: false }));
  }
}

function* getUserRequest(action) {
  try {
    yield put(getUser.request({ isLoading: true }));

    const { currentAppSource } = action.payload;
    const response = yield call(getUserCall, currentAppSource);
    if (response?.success !== false) {
      const user = response;
      const allowUsers = [USER_ROLE.ADMIN, USER_ROLE.STAFF];
      if (allowUsers.includes(user.role)) {
        yield put(getUser.success({ user }));
      } else {
        yield put(replace(SMKLinks.LOGIN_PAGE));
      }
    }
  } catch (error) {
    yield put(getUser.failure(error));
  } finally {
    yield put(getUser.fulfill({ isLoading: false }));
  }
}

export function* doLogoutRequest() {
  try {
    const auth = new AuthService();

    yield put(doLogout.request({ isLoading: true }));

    const response = yield call(logoutCall);

    auth.unsetSessionCookie();
    auth.unsetRefreshSessionCookie();
    auth.unsetAppSourceCookie();

    yield put(doLogout.success());
    yield put(replace(SMKLinks.LOGIN_PAGE));
  } catch (error) {
    yield put(doLogout.failure(error));
  } finally {
    yield put(doLogout.fulfill({ isLoading: false }));
  }
}

function* doSocialLoginRequest(action) {
  try {
    const auth = new AuthService();

    yield put(doSocialLogin.request({ isLoading: true }));

    const removeAllCookies = () => {
      auth.unsetSessionCookie();
      auth.unsetRefreshSessionCookie();
      auth.unsetAppSourceCookie();
    };

    const { userObj, userType } = action.payload;

    const response = yield call(socialLoginCall, userObj, userType);

    if (response?.success === false) {
      const { message } = response;
      yield put(doSocialLogin.failure(message?.message));
    } else {
      const { accessToken, refreshToken } = response;

      if (accessToken && refreshToken) {
        auth.setSessionCookie(accessToken);
        auth.setRefreshSessionCookie(refreshToken);

        const user = yield call(getUserCall, userType);

        if (!isEmpty(user)) {
          auth.setAppSourceCookie(user?.appSource);

          const allowUsers = [USER_ROLE.ADMIN, USER_ROLE.STAFF];
          if (allowUsers.includes(user.role)) {
            yield put(doLogin.success({ token: accessToken }));
            yield put(getUser.success({ user, currentAppSource: user?.appSource }));

            yield put(replace(SMKLinks.DASHBOARD));
          } else {
            alert('Not Authorised');
            removeAllCookies();
          }
        } else {
          removeAllCookies();
        }
      }
    }
  } catch (error) {
    yield put(doSocialLogin.failure(error));
  } finally {
    yield put(doSocialLogin.fulfill({ isLoading: false }));
  }
}
function* doHRLoginRequest(action) {
  try {
    const auth = new AuthService();

    yield put(doHRLogin.request({ isLoading: true }));

    const { userObj, userType } = action.payload;

    const response = yield call(socialLoginCall, userObj, userType);

    const removeAllCookies = () => {
      auth.unsetSessionCookie();
      auth.unsetRefreshSessionCookie();
      auth.unsetAppSourceCookie();
    };

    if (response?.success === false) {
      const { message } = response;
      yield put(doHRLogin.failure(message?.message));
    } else {
      const { accessToken, refreshToken } = response;
      if (accessToken && refreshToken) {
        auth.setSessionCookie(accessToken);
        auth.setRefreshSessionCookie(refreshToken);
        const user = yield call(getUserCall, userType);
        auth.setAppSourceCookie(user?.appSource);

        const isHRRole = user?.roles?.find((e) => e?.name?.toLowerCase() === 'hr');

        if (!isEmpty(isHRRole)) {
          yield put(getUser.success({ user, currentAppSource: user?.appSource }));
          yield put(replace(SMKLinks.HR_DASHBOARD));
        } else {
          alert('Not Authorised to access HR Dashboard');
          removeAllCookies();
        }
      }
    }
  } catch (error) {
    yield put(doHRLogin.failure(error));
  } finally {
    yield put(doHRLogin.fulfill({ isLoading: false }));
  }
}

export default function* authSaga() {
  yield takeLatest(doLogin.TRIGGER, doLoginRequest);
  yield takeLatest(doSocialLogin.TRIGGER, doSocialLoginRequest);
  yield takeLatest(doHRLogin.TRIGGER, doHRLoginRequest);
  yield takeLatest(getUser.TRIGGER, getUserRequest);
  yield takeLatest(doLogout.TRIGGER, doLogoutRequest);
}
